import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import $ from "jquery";
import "daterangepicker";
import "daterangepicker/daterangepicker.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MobileLayout from "../../../layouts/MobileLayout";
import { alignPickerToFilter } from "../../../utils/mobileDatePicker";
import { TeamAttendanceView } from "../../../components/mobile/AttendanceRecords";
import { api } from "../../../utils/api";
import { useAuthStore } from "../../../store/authStore";
import { useLoaderStore } from "../../../store/loaderStore";

const TeamAttendance = () => {
  const navigate = useNavigate();

  const { user } = useAuthStore();
  const userId = user?.user_id;
  const companyId = user?.company_id;
  const { showLoader, hideLoader } = useLoaderStore();

  const pickerRef = useRef(null);

  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  );

  const [endDate, setEndDate] = useState(
    moment().endOf("month").format("YYYY-MM-DD"),
  );

  // Members picked in the search (multi-select)
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  // Direct reports of the logged-in manager
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersStatus, setTeamMembersStatus] = useState("loading");
  const [teamAttendanceData, setTeamAttendanceData] = useState([]);

  // ── Fetch subordinates ──
  useEffect(() => {
    if (!userId || !companyId) return;

    let active = true;
    setTeamMembersStatus("loading");

    const fetchSubordinates = async () => {
      showLoader("master");
      try {
        const response = await api.get(
          `/user/subordinates/${userId}/${companyId}`,
        );
        const result = await response.json();

        const list = Array.isArray(result?.subordinates)
          ? result.subordinates
          : [];

        const mapped = list.map((u) => ({
          id: u.user_id,
          name: `${u.first_name || ""} ${u.last_name || ""}`.trim() || "-",
          department: u.designation || "",
          employee_code: u.employee_code || "",
        }));

        if (!active) return;
        setTeamMembers(mapped);
        setTeamMembersStatus("ready");
      } catch (error) {
        if (!active) return;
        setTeamMembersStatus("error");
        console.error("SUBORDINATES ERROR:", error);
        toast.error(error.message || "Failed to load team members");
        setTeamMembers([]);
      } finally {
        hideLoader();
      }
    };

    fetchSubordinates();
    return () => {
      active = false;
      hideLoader();
    };
  }, [userId, companyId, showLoader, hideLoader]);

  // ── Fetch attendance log for each subordinate in the selected date range ──
  useEffect(() => {
    if (!companyId || teamMembers.length === 0) {
      setTeamAttendanceData([]);
      return;
    }

    const fetchAttendance = async () => {
      try {
        const params = new URLSearchParams();

        params.append("company_id", companyId);
        params.append("from_date", startDate);
        params.append("to_date", endDate);

        teamMembers.forEach((member) => {
          params.append("user_id", member.id);
        });

        const response = await api.get(
          `/attendance/attendance-log?${params.toString()}`,
        );
        const result = await response.json();

        const apiRows = Array.isArray(result?.data) ? result.data : [];

        const mapped = apiRows.map((row) => ({
          user_id: row.user_id,
          user_name: row.employee_name,
          date: row.date,
          first_punch: row.first_punch,
          last_punch: row.last_punch,
          type: row.type,
        }));

        setTeamAttendanceData(mapped);
      } catch (error) {
        console.error("TEAM ATTENDANCE ERROR:", error);
        toast.error(error.message || "Failed to load attendance");
        setTeamAttendanceData([]);
      }
    };

    fetchAttendance();
  }, [companyId, startDate, endDate, teamMembers]);

  useEffect(() => {
    const $picker = $(pickerRef.current);

    if ($picker.data("daterangepicker")) {
      $picker.data("daterangepicker").remove();
    }

    $picker.daterangepicker({
      startDate: moment(startDate),
      endDate: moment(endDate),
      autoUpdateInput: false,
      showDropdowns: true,
      linkedCalendars: false,
      alwaysShowCalendars: true,
      opens: "left",
      ranges: {
        Today: [moment(), moment()],
        Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [
          moment().subtract(1, "month").startOf("month"),
          moment().subtract(1, "month").endOf("month"),
        ],
      },
    });

    const picker = $picker.data("daterangepicker");

    picker.container.addClass("mobile-daterangepicker");

    // popup sits right under the filter card, at the card's width
    const unalign = alignPickerToFilter(picker, pickerRef.current);

    $picker
      .off("apply.daterangepicker")
      .on("apply.daterangepicker", function (ev, picker) {
        const from = picker.startDate.format("YYYY-MM-DD");
        const to = picker.endDate.format("YYYY-MM-DD");

        setStartDate(from);
        setEndDate(to);
      });

    return () => {
      unalign();

      if ($picker.data("daterangepicker")) {
        $picker.data("daterangepicker").remove();
      }
    };
  }, []);

  // ── Attendance rows for the selected members (all members if none selected),
  // sorted date-wise so entries interleave chronologically ──
  const displayedAttendance = (
    selectedUserIds.length > 0
      ? teamAttendanceData.filter((row) => selectedUserIds.includes(row.user_id))
      : teamAttendanceData
  )
    .slice()
    .sort((a, b) => {
      if (a.date !== b.date) {
        return a.date < b.date ? -1 : 1;
      }
      return String(a.user_name || "").localeCompare(String(b.user_name || ""));
    });

  const toggleSelectMember = (member) => {
    setSelectedUserIds((prev) =>
      prev.includes(member.id)
        ? prev.filter((id) => id !== member.id)
        : [...prev, member.id],
    );
  };

  const removeSelectedMember = (memberId) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== memberId));
  };

  return (
    <MobileLayout>
      <TeamAttendanceView
        pickerRef={pickerRef}
        startDate={startDate}
        endDate={endDate}
        members={teamMembers}
        membersStatus={teamMembersStatus}
        selectedUserIds={selectedUserIds}
        onToggleMember={toggleSelectMember}
        onRemoveMember={removeSelectedMember}
        onClearSelection={() => setSelectedUserIds([])}
        records={displayedAttendance}
        onBack={() => navigate(-1)}
      />
    </MobileLayout>
  );
};

export default TeamAttendance;